import * as Sentry from '@sentry/nextjs';

import { APIRequestLabel } from '@consts';
import {
  ChatbotDataEntryModel,
  ChatbotSettingsModel,
  ChatbotTestRequestModel,
  VersionedEntityType
} from '@models';
import { callHttpDelete, callHttpGet, callHttpPost, callHttpPut } from 'lib/http';

const createChatbotDataEntry = (get, set) => {
  return async (chatbotEntityId: string, payload: ChatbotDataEntryModel) => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_CHATBOT_DATA_ENTRY,
      `/chatbots/${chatbotEntityId}/entries`,
      payload
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_CHATBOT_DATA_ENTRY];

    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_CHATBOT_DATA_ENTRY,
      `/chatbots/${chatbotEntityId}/entries/${apiCallStatus.response.entityId}?maxVersionType=${VersionedEntityType.DRAFT}`
    );
    let getApiCallStatus = get().apiCallStatus[APIRequestLabel.GET_CHATBOT_DATA_ENTRY];

    if (getApiCallStatus.error) {
      Sentry.captureMessage(getApiCallStatus.error);
      return;
    }

    // update entries
    let entries = get().chatbotDataEntries || [];
    entries.push(getApiCallStatus.response);
    set({ chatbotDataEntries: [...entries] });
  };
};

const getChatbotDataEntries = (get, set) => {
  return async (params: {
    chatbotEntityId: string;
    maxVersionType?: VersionedEntityType;
    semanticQuery?: string;
  }) => {
    let qParams = {};
    if (params.maxVersionType) {
      qParams['maxVersionType'] = params.maxVersionType;
    }

    if (params.semanticQuery) {
      qParams['query'] = params.semanticQuery;
    }

    let q = new URLSearchParams(qParams).toString();
    let url = `/chatbots/${params.chatbotEntityId}/entries?${q}`;

    await callHttpGet(get, set, APIRequestLabel.GET_CHATBOT_DATA_ENTRIES, url);

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_CHATBOT_DATA_ENTRIES];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({ chatbotDataEntries: apiCallStatus.response?.entries });
  };
};

const updateChatbotDataEntry = (get, set) => {
  return async (
    chatbotEntityId: string,
    entryEntityId: string,
    payload: ChatbotDataEntryModel
  ): Promise<void> => {
    await callHttpPut(
      get,
      set,
      APIRequestLabel.UPDATE_CHATBOT_DATA_ENTRY,
      `/chatbots/${chatbotEntityId}/entries/${entryEntityId}`,
      payload
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_CHATBOT_DATA_ENTRY];

    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_CHATBOT_DATA_ENTRY,
      `/chatbots/${chatbotEntityId}/entries/${entryEntityId}?maxVersionType=${VersionedEntityType.DRAFT}`
    );
    let getApiCallStatus = get().apiCallStatus[APIRequestLabel.GET_CHATBOT_DATA_ENTRY];

    if (getApiCallStatus.error) {
      Sentry.captureMessage(getApiCallStatus.error);
      return;
    }

    // update entries
    let entries = get().chatbotDataEntries;
    let idx = entries.findIndex((e) => e.entityId === entryEntityId);
    if (idx > -1) {
      entries[idx] = { ...getApiCallStatus.response };
      set({ chatbotDataEntries: [...entries] });
    }
  };
};

const deleteChatbotDataEntry = (get, set) => {
  return async (chatbotEntityId: string, entryEntityId: string): Promise<void> => {
    await callHttpDelete(
      get,
      set,
      APIRequestLabel.DELETE_CHATBOT_DATA_ENTRY,
      `/chatbots/${chatbotEntityId}/entries/${entryEntityId}`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_CHATBOT_DATA_ENTRY];

    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    // delete entry
    let entries = get().chatbotDataEntries;
    let idx = entries.findIndex((e) => e.entityId === entryEntityId);
    if (idx > -1) {
      entries.splice(idx, 1);
      set({ chatbotDataEntries: [...entries] });
    }
  };
};

const getChatbotSettings = (get, set) => {
  return async (chatbotEntityId: string): Promise<void> => {
    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_CHATBOT_SETTINGS,
      `/chatbots/${chatbotEntityId}/settings`
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_CHATBOT_SETTINGS];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({ chatbotSettings: apiCallStatus.response });
  };
};

const updateChatbotSettings = (get, set) => {
  return async (chatbotEntityId: string, payload): Promise<void> => {
    await callHttpPut(
      get,
      set,
      APIRequestLabel.UPDATE_CHATBOT_SETTINGS,
      `/chatbots/${chatbotEntityId}/settings`,
      payload
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_CHATBOT_SETTINGS];

    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({ chatbotDataEntries: apiCallStatus.response });
  };
};

const setChatbotSettings = (get, set) => {
  return (payload: ChatbotSettingsModel): void => {
    set({ chatbotSettings: payload });
  };
};

const createChatbotTestRequest = (get, set) => {
  return async (
    chatbotEntityId: string,
    payload: ChatbotTestRequestModel,
    params: { chatbotMaxVersionType?: VersionedEntityType }
  ): Promise<void> => {
    params = params || {};
    let qParams = {};
    if (params.chatbotMaxVersionType) {
      qParams['chatbotMaxVersionType'] = params.chatbotMaxVersionType;
    }
    let q = new URLSearchParams(qParams).toString();

    await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_CHATBOT_TEST_REQUEST,
      `/chatbots/${chatbotEntityId}/test?${q}`,
      payload
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_CHATBOT_TEST_REQUEST];

    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({
      chatbotTestRequests: new Map(get().chatbotTestRequests).set(chatbotEntityId, {
        ...payload,
        id: apiCallStatus.response.id
      })
    });
  };
};
const updateChatbotTestRequest = (get, set) => {
  return async (
    chatbotEntityId: string,
    requestId: number,
    payload: ChatbotTestRequestModel
  ): Promise<void> => {
    await callHttpPut(
      get,
      set,
      APIRequestLabel.UPDATE_CHATBOT_TEST_REQUEST,
      `/chatbots/${chatbotEntityId}/test/${requestId}`,
      payload
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_CHATBOT_TEST_REQUEST];

    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }
    let currentRequest = get().chatbotTestRequests.get(chatbotEntityId);
    set({
      chatbotTestRequests: new Map(get().chatbotTestRequests).set(chatbotEntityId, {
        ...currentRequest,
        ...payload
      })
    });
  };
};

const getChatbotTestRequest = (get, set) => {
  return async (chatbotEntityId: string, requestId: number): Promise<void> => {
    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_CHATBOT_TEST_REQUEST,
      `/chatbots/${chatbotEntityId}/test/${requestId}`
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_CHATBOT_TEST_REQUEST];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({
      chatbotTestRequests: new Map(get().chatbotTestRequests).set(chatbotEntityId, {
        ...apiCallStatus.response
      })
    });
  };
};

const setChatbotTestRequest = (get, set) => {
  return (chatbotEntityId: string, payload: ChatbotTestRequestModel): void => {
    set({ chatbotTestRequests: new Map(get().chatbotTestRequests).set(chatbotEntityId, payload) });
  };
};

const publishChatbot = (get, set) => {
  return async (chatbotEntityId: string): Promise<void> => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.PUBLISH_CHATBOT,
      `/chatbots/${chatbotEntityId}/publish`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.PUBLISH_CHATBOT];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

export {
  createChatbotDataEntry,
  createChatbotTestRequest,
  deleteChatbotDataEntry,
  getChatbotDataEntries,
  getChatbotSettings,
  getChatbotTestRequest,
  publishChatbot,
  setChatbotSettings,
  setChatbotTestRequest,
  updateChatbotDataEntry,
  updateChatbotSettings,
  updateChatbotTestRequest
};
